import { api } from '@/shared/lib/axios';

import type { ChatConversation, ChatMessage } from '../data/types';

// All `/api/chat/*` endpoints. Response shapes mirror api/'s controllers — kept
// hand-typed (no codegen) per the project's "Zod near the consumer" convention.

interface ListConversationsResponse {
  conversations: ChatConversation[];
  total: number;
}

export async function listConversations(params?: {
  limit?: number;
  skip?: number;
}): Promise<ListConversationsResponse> {
  const res = await api.get<ListConversationsResponse>('/api/chat/conversations', {
    params: { status: 'active', sortBy: 'updatedAt', sortOrder: 'desc', ...params },
  });
  return res.data;
}

interface GetConversationResponse {
  conversation: ChatConversation & { _id?: string };
  gemSlug: string | null;
}

export async function getConversation(
  id: string,
): Promise<{ conversation: ChatConversation; gemSlug: string | null }> {
  const res = await api.get<GetConversationResponse>(`/api/chat/conversations/${id}`);
  const { conversation, gemSlug } = res.data;
  return {
    conversation: normalizeConversation(conversation),
    gemSlug,
  };
}

interface ListMessagesResponse {
  messages: Array<Omit<ChatMessage, 'status'> & { status: string }>;
  total: number;
}

export async function listMessages(conversationId: string): Promise<ChatMessage[]> {
  const res = await api.get<ListMessagesResponse>(
    `/api/chat/conversations/${conversationId}/messages`,
    { params: { limit: 100 } },
  );
  return res.data.messages.map(normalizeMessage);
}

export async function setMessageFeedback(
  conversationId: string,
  messageId: string,
  feedback: 'positive' | 'negative' | null,
): Promise<void> {
  await api.post(`/api/chat/conversations/${conversationId}/messages/${messageId}/feedback`, {
    feedback,
  });
}

// Soft-deletes a conversation server-side — it drops out of the active list.
export async function deleteConversation(id: string): Promise<void> {
  await api.delete(`/api/chat/conversations/${id}`);
}

// Renames a conversation. Server caps the title at 100 chars (rejects empty).
export async function renameConversation(id: string, title: string): Promise<void> {
  await api.patch(`/api/chat/conversations/${id}`, { title });
}

// Orphans a conversation from the current owner — used when "Save chat history"
// is off and the user leaves the chat. The conversation stays in the DB (so
// token consumption + analytics are intact) but disappears from the user's list.
export async function detachConversation(id: string): Promise<void> {
  await api.post(`/api/chat/conversations/${id}/detach`);
}

// Owner-scoped share state — mirrors api/'s ShareState shape.
export interface ShareState {
  shareSlug: string | null;
  isShared: boolean;
  shareViewCount: number;
  sharedAt: string | null;
}

// The share endpoints wrap their payload in `{ share }`, matching the chat
// controller's named-key convention (`{ conversation }`, `{ message }`).
interface ShareResponse {
  share: ShareState;
}

// Creates (or, with `regenerate`, rotates) the public share link for a
// conversation. Side effect: sets `isShared = true` server-side.
export async function createShareLink(
  id: string,
  opts?: { regenerate?: boolean },
): Promise<ShareState> {
  const res = await api.post<ShareResponse>(`/api/chat/conversations/${id}/share`, {
    regenerate: opts?.regenerate ?? false,
  });
  return res.data.share;
}

// Toggles whether the existing share link resolves publicly. Re-enabling reuses
// the same slug — only `createShareLink({ regenerate: true })` rotates it.
export async function updateShareAccess(id: string, isShared: boolean): Promise<ShareState> {
  const res = await api.patch<ShareResponse>(`/api/chat/conversations/${id}/share`, { isShared });
  return res.data.share;
}

function normalizeConversation(c: ChatConversation & { _id?: string }): ChatConversation {
  // Tolerate either `id` or legacy `_id` so a backend shape change doesn't
  // require redeploying the UI in lockstep.
  return { ...c, id: c.id ?? (c._id as string) };
}

function normalizeMessage(m: Omit<ChatMessage, 'status'> & { status: string }): ChatMessage {
  // Server 'failed' renders as the client error bubble; 'regenerated' (an
  // older branch variant) stays displayable as 'completed'.
  let status: ChatMessage['status'];
  if (m.status === 'failed') status = 'error';
  else if (
    m.status === 'completed' ||
    m.status === 'streaming' ||
    m.status === 'error' ||
    m.status === 'pending'
  )
    status = m.status;
  else status = 'completed';
  return { ...m, status };
}
